import { ChevronDownIcon } from 'lucide-react';

import React from 'react';

type InlineSelectOption = { label: string; value: string | number };

type InlineSelectProps = {
	id?: string;
	label?: string;
	value: string | number;
	options: ReadonlyArray<InlineSelectOption>;
	onChange: React.ChangeEventHandler<HTMLSelectElement>;
	disabled?: boolean;
	maxWidth?: string;
	/** Shown as a disabled first option when value is an empty string. */
	placeholder?: string;
};

/**
 * A ghost inline select that auto-sizes to the selected option's label width.
 * Renders a visible prefix label (e.g. "Timezone:") followed by the native select
 * positioned over an invisible sizer span.
 */
function InlineSelect({
	id,
	label,
	value,
	options,
	onChange,
	disabled,
	maxWidth = '150px',
	placeholder,
}: InlineSelectProps) {
	const isUnselected = value === '' && !!placeholder;
	const selectedLabel =
		options.find((o) => String(o.value) === String(value))?.label ?? (isUnselected ? placeholder : '');

	return (
		<div className="flex items-center gap-1">
			{label ? <span className="text-xs text-slate-400">{label}</span> : null}
			<div className="relative flex items-center" style={{ maxWidth }}>
				{/* Invisible sizer — stretches the container to the selected label's width */}
				<span aria-hidden className="invisible truncate whitespace-nowrap pr-4 text-xs">
					{selectedLabel}
				</span>
				<select
					id={id}
					value={value}
					onChange={onChange}
					disabled={disabled}
					className="absolute inset-0 w-full cursor-pointer appearance-none border-none bg-transparent pr-4 text-xs text-slate-500 outline-none hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
				>
					{placeholder && (
						<option value="" disabled>
							{placeholder}
						</option>
					)}
					{options.map((opt) => (
						<option key={opt.value} value={opt.value}>
							{opt.label}
						</option>
					))}
				</select>
				<ChevronDownIcon className="pointer-events-none absolute right-0 size-3 text-slate-400" />
			</div>
		</div>
	);
}

export { InlineSelect, type InlineSelectOption, type InlineSelectProps };
